import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { Page, Section } from '../components/UI';
import ContractBody, { type ContractPackage } from '../components/contracts/ContractBody';
import { calculateClientMonthlyFee, resolvePriceIsNewCustomer, resolveTarifTier } from '../utils/clientPricing';
import {
    resolveTarifBadgeDisplay,
    resolveVertragsbeginnDisplay,
    type MandantTarifStatus,
} from '../utils/mandantNrFormat';
import { defaultSettings } from '../store/slices/settingsSlice';
import {
    buildPremiumContractLines,
    formatPremiumActivatedDate,
    PREMIUM_CONTRACT_BILLING_ENABLED,
} from '../utils/premiumCostBridge';

interface VertragsuebersichtPageProps {
    showNotification?: (msg: string, type: 'success' | 'error') => void;
}

const STATUS_TO_PACKAGE: Record<MandantTarifStatus, ContractPackage> = {
    Solo: 'solo',
    TeamBase: 'teamBase',
    TeamPro: 'teamPro',
    'TeamPro+': 'teamProPlus',
};

const flagTrue = (v: unknown) =>
    v === true || v === 1 || v === '1' || v === 'true';

const readNeukundenPct = (settings: any): number => {
    const raw = Number(settings?.global?.Neukunden_Aufschlag);
    let pct =
        Number.isFinite(raw) && raw > 0
            ? raw
            : Number(defaultSettings.global.Neukunden_Aufschlag ?? 30);
    if (pct > 0 && pct < 1) pct = pct * 100;
    return pct;
};

// ============================================================================
// COMBINED STYLES: CARBON INJECTION + EXECUTIVE CARBON
// ============================================================================
const CombinedStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

        .led-gold { color: #E6C299; text-shadow: 0 0 10px rgba(230,194,153,0.6); }

        .carbon-weave-bg-dark {
            background-color: #0a0b0e !important;
            background-image: 
                radial-gradient(circle at 100% 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #14161f 10%, #08090c 10%, #08090c 23%, #14161f 23%, #14161f 30%, #08090c 30%, #08090c 43%, #08090c 43%, #14161f 50%, #08090c 50%, #08090c 63%, #14161f 63%, #14161f 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #14161f 5%, #08090c 5%, #08090c 15%, #14161f 15%, #14161f 20%, #08090c 20%, #08090c 29%, #14161f 29%, #14161f 34%, #08090c 34%, #08090c 44%, #14161f 44%, #14161f 49%, transparent 49%, transparent) !important;
            background-size: 16px 16px !important;
        }

        .carbon-weave-bg-standard {
            background-color: #111;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative !important;
            border-radius: 1.5rem;
            border: 1px solid #20232b !important;
            box-shadow: 0 15px 35px -10px rgba(0,0,0,0.9), inset 0 1px 2px rgba(255,255,255,0.05) !important;
        }

        .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4));
            border-radius: inherit;
            pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        .metal-badge {
            background: linear-gradient(to bottom, #222, #0a0a0a);
            border: 1px solid #333;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 10px rgba(0,0,0,0.8);
            border-radius: 6px;
        }

        .contract-carbon-shell {
            background: #030303 !important;
            background-color: #030303 !important;
            min-height: 100vh;
        }
        
        body:has(.contract-carbon-shell) {
            background: #030303 !important;
        }

        /* ============================================================
           LIGHT-MODE OVERRIDES (Ring 4b) — Dark Mode bleibt pixelidentisch,
           da alle Regeln nur unter html:not(.dark) greifen.
           ============================================================ */
        html:not(.dark) .led-gold { color: #8D6435; text-shadow: none; }
        html:not(.dark) .carbon-weave-bg-dark {
            background-color: #FFFFFF !important;
            background-image: none !important;
        }
        html:not(.dark) .carbon-weave-bg-standard {
            background-color: #FFFFFF;
            background-image: none;
        }
        html:not(.dark) .carbon-panel {
            border: 1px solid #D6D3D1 !important;
            box-shadow: 0 10px 30px -12px rgba(28,25,23,0.15) !important;
        }
        html:not(.dark) .carbon-panel::after { background: none; }
        html:not(.dark) .metal-badge {
            background: linear-gradient(to bottom, #FFFFFF, #F1EFEA);
            border: 1px solid #D6D3D1;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 4px 10px rgba(28,25,23,0.08);
        }
        html:not(.dark) .contract-carbon-shell {
            background: #F4F2EF !important;
            background-color: #F4F2EF !important;
        }
        html:not(.dark) body:has(.contract-carbon-shell) {
            background: #F4F2EF !important;
        }
    `}</style>
);

type CloudFeeFlags = {
    forceNewCustomerPremium: boolean;
    disableNewCustomerPremium: boolean;
    neukundenManualOverride: boolean;
    isPremium2027: boolean;
    monthlyFee: number;
    billingStatus?: string | null;
    isExistingCustomer?: boolean | null;
    isBestandskunde?: boolean | null;
    packageTier?: string | null;
    tarifStatus?: string | null;
    customerNumber?: string | null;
    hasEmployeeEval?: boolean | null;
    isSolo?: boolean | null;
    vertragsbeginn?: string | null;
    mandantNr?: string | null;
    loaded: boolean;
};

export default function VertragsuebersichtPage({ showNotification }: VertragsuebersichtPageProps) {
    const { clientData, settings, team, unlockedPremiumTools } = useAppStore(state => ({ 
        clientData: state.clientData, 
        settings: state.settings,
        team: state.team,
        unlockedPremiumTools: state.unlockedPremiumTools,
    }));

    const [cloudFlags, setCloudFlags] = useState<CloudFeeFlags>({
        forceNewCustomerPremium: false,
        disableNewCustomerPremium: false,
        neukundenManualOverride: false,
        isPremium2027: false,
        monthlyFee: 0,
        billingStatus: null,
        isExistingCustomer: null,
        isBestandskunde: null,
        packageTier: null,
        tarifStatus: null,
        customerNumber: null,
        hasEmployeeEval: null,
        isSolo: null,
        vertragsbeginn: null,
        mandantNr: null,
        loaded: false,
    });

    // Direkter Firestore-Read — unabhängig vom Store-Merge (God-Mode / Sync-Lücken)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            let uid: string | null = null;
            try {
                uid = sessionStorage.getItem('GOD_MODE_ID');
            } catch {
                uid = null;
            }
            if (!uid) {
                try {
                    const { auth } = await import('../config/firebase');
                    uid = auth.currentUser?.uid || null;
                } catch {
                    uid = null;
                }
            }
            if (!uid) {
                if (!cancelled) setCloudFlags((s) => ({ ...s, loaded: true }));
                return;
            }
            try {
                const [snap, userSnap] = await Promise.all([
                    getDoc(doc(db, 'kalkulation_mandanten', uid)),
                    getDoc(doc(db, 'users', uid)).catch(() => null as any),
                ]);
                if (cancelled) return;
                if (!snap.exists()) {
                    setCloudFlags((s) => ({ ...s, loaded: true }));
                    return;
                }
                const d = snap.data() || {};
                const cd = d.clientData || {};
                const u =
                    userSnap && typeof userSnap.exists === 'function' && userSnap.exists()
                        ? userSnap.data() || {}
                        : {};
                // Root → clientData → users/{uid} (Registrierung überlebt Sync-WIEDERHERGESTELLT)
                const pick = (a: unknown, b: unknown, c?: unknown) => {
                    const bad = (v: unknown) =>
                        v === undefined || v === null || v === '' || v === 'WIEDERHERGESTELLT';
                    if (!bad(a)) return a;
                    if (!bad(b)) return b;
                    if (!bad(c)) return c;
                    return a !== undefined && a !== null ? a : b !== undefined && b !== null ? b : c;
                };
                let force = flagTrue(
                    pick(d.forceNewCustomerPremium, cd.forceNewCustomerPremium)
                );
                let disable = flagTrue(
                    pick(d.disableNewCustomerPremium, cd.disableNewCustomerPremium)
                );
                if (force && disable) {
                    force = false;
                    disable = false;
                }
                const premium2027 = flagTrue(pick(d.isPremium2027, cd.isPremium2027));
                const manualOverride = flagTrue(
                    pick(d.neukundenManualOverride, cd.neukundenManualOverride)
                );
                const feeRaw = d.monthlyFee ?? cd.monthlyFee ?? 0;
                const fee =
                    typeof feeRaw === 'number'
                        ? feeRaw
                        : parseFloat(String(feeRaw).replace(',', '.')) || 0;
                const billingStatus =
                    (pick(d.billingStatus, cd.billingStatus, u.billingStatus) as string | null | undefined) ?? null;
                const isExistingCustomerRaw = pick(d.isExistingCustomer, cd.isExistingCustomer, u.isExistingCustomer);
                const isBestandskundeRaw = pick(d.isBestandskunde, cd.isBestandskunde, u.isBestandskunde);
                const packageTierVal = pick(d.packageTier, cd.packageTier, u.packageTier || u.tarifStatus);
                const tarifStatusVal = pick(d.tarifStatus, cd.tarifStatus, u.tarifStatus);
                const hasEvalVal = pick(d.hasEmployeeEval, cd.hasEmployeeEval, u.hasEmployeeEval);
                const isSoloVal = pick(d.isSolo, cd.isSolo, u.isSolo);
                const vertragsbeginnVal = pick(d.vertragsbeginn, cd.vertragsbeginn, u.vertragsbeginn);
                const mandantNrVal = pick(d.mandantNr, cd.mandantNr, u.mandantNr);
                const customerNumberVal = pick(d.customerNumber, cd.customerNumber, u.customerNumber);

                // Auto-Heal: Sync hat Tarif/Nr. zerstört → aus users/{uid} zurückschreiben
                const rootNr = String(d.mandantNr || '').trim();
                const hasUserTarif = !!(u.tarifStatus || u.packageTier || u.hasEmployeeEval === true || u.hasEmployeeEval === false);
                const needsHeal =
                    !sessionStorage.getItem('GOD_MODE_ID') &&
                    hasUserTarif &&
                    (
                        rootNr === 'WIEDERHERGESTELLT' ||
                        rootNr === '' ||
                        (!d.packageTier && !d.tarifStatus && d.hasEmployeeEval !== true && d.hasEmployeeEval !== false)
                    );
                if (needsHeal) {
                    try {
                        const healedTarif = String(
                            u.packageTier ||
                                u.tarifStatus ||
                                (u.isSolo === true ? 'Solo' : u.hasEmployeeEval === true ? 'TeamPro' : 'TeamBase')
                        );
                        const now = new Date();
                        const coreDefault = `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
                        const vb = String(u.vertragsbeginn || vertragsbeginnVal || coreDefault);
                        const coreFromVb = /^\d{2}\/\d{4}$/.test(vb) ? vb : coreDefault;
                        const healedNr =
                            rootNr && rootNr !== 'WIEDERHERGESTELLT' && /MA\s+-\s+\d{2}\/\d{4}/i.test(rootNr)
                                ? `${rootNr.replace(/\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i, '').trim()} - ${healedTarif}`
                                : `MA - ${coreFromVb} - ${healedTarif}`;
                        await setDoc(
                            doc(db, 'kalkulation_mandanten', uid),
                            {
                                mandantNr: healedNr,
                                packageTier: healedTarif,
                                tarifStatus: healedTarif,
                                hasEmployeeEval: healedTarif === 'TeamPro' || healedTarif === 'TeamPro+',
                                isSolo: healedTarif === 'Solo',
                                vertragsbeginn: vb,
                                customerNumber: d.customerNumber || u.customerNumber || 'PENDING',
                                billingStatus: d.billingStatus || u.billingStatus || 'pending_approval',
                                isExistingCustomer: u.isExistingCustomer === true,
                                isBestandskunde: u.isBestandskunde === true || u.isExistingCustomer === true,
                                'clientData.mandantNr': healedNr,
                                'clientData.packageTier': healedTarif,
                                'clientData.tarifStatus': healedTarif,
                                'clientData.hasEmployeeEval': healedTarif === 'TeamPro' || healedTarif === 'TeamPro+',
                                'clientData.isSolo': healedTarif === 'Solo',
                                'clientData.vertragsbeginn': vb,
                            },
                            { merge: true }
                        );
                    } catch (healErr) {
                        console.warn('Vertragsuebersicht: Tarif-Auto-Heal fehlgeschlagen', healErr);
                    }
                }

                setCloudFlags({
                    forceNewCustomerPremium: force,
                    disableNewCustomerPremium: disable,
                    neukundenManualOverride: manualOverride,
                    isPremium2027: premium2027,
                    monthlyFee: fee,
                    billingStatus: billingStatus != null ? String(billingStatus) : null,
                    isExistingCustomer:
                        isExistingCustomerRaw === true
                            ? true
                            : isExistingCustomerRaw === false
                              ? false
                              : null,
                    isBestandskunde:
                        isBestandskundeRaw === true
                            ? true
                            : isBestandskundeRaw === false
                              ? false
                              : null,
                    packageTier:
                        packageTierVal != null && String(packageTierVal).trim()
                            ? String(packageTierVal).trim()
                            : null,
                    tarifStatus:
                        tarifStatusVal != null && String(tarifStatusVal).trim()
                            ? String(tarifStatusVal).trim()
                            : null,
                    customerNumber:
                        customerNumberVal != null && String(customerNumberVal).trim()
                            ? String(customerNumberVal).trim()
                            : null,
                    hasEmployeeEval:
                        hasEvalVal === true ? true : hasEvalVal === false ? false : null,
                    isSolo: isSoloVal === true ? true : isSoloVal === false ? false : null,
                    vertragsbeginn:
                        vertragsbeginnVal != null && String(vertragsbeginnVal).trim()
                            ? String(vertragsbeginnVal).trim()
                            : null,
                    mandantNr:
                        mandantNrVal != null && String(mandantNrVal).trim()
                            ? String(mandantNrVal).trim()
                            : null,
                    loaded: true,
                });
            } catch (e) {
                console.error('Vertragsuebersicht: Cloud-Flags lesen fehlgeschlagen', e);
                if (!cancelled) setCloudFlags((s) => ({ ...s, loaded: true }));
            }
        })();
        return () => {
            cancelled = true;
        };
    // Cloud-Flags bei Schalter-Änderung am Root erneut lesen (nicht nur bei Mandanten-Nr.)
    }, [
        clientData?.mandantNr,
        clientData?.forceNewCustomerPremium,
        clientData?.disableNewCustomerPremium,
        clientData?.neukundenManualOverride,
        clientData?.isBestandskunde,
        clientData?.isExistingCustomer,
        clientData?.billingStatus,
    ]);

    // --- BERECHNUNGEN FÜR DEN ÜBERSICHTS-BEREICH (TOP) ---
    const isBasic = !!clientData?.basicChecked || clientData?.lizenzDetails?.typ === 'basic';
    // Root-Tarif aus Cloud hat Vorrang (Badge-Funktion liest packageTier/tarifStatus strikt)
    const tarifClientSource = {
        ...(clientData || {}),
        ...(cloudFlags.loaded && cloudFlags.packageTier
            ? { packageTier: cloudFlags.packageTier }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.tarifStatus
            ? { tarifStatus: cloudFlags.tarifStatus }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.customerNumber
            ? { customerNumber: cloudFlags.customerNumber }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.isExistingCustomer !== null
            ? { isExistingCustomer: cloudFlags.isExistingCustomer }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.isBestandskunde !== null
            ? { isBestandskunde: cloudFlags.isBestandskunde }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.hasEmployeeEval !== null
            ? { hasEmployeeEval: cloudFlags.hasEmployeeEval }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.isSolo !== null
            ? { isSolo: cloudFlags.isSolo }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.billingStatus
            ? { billingStatus: cloudFlags.billingStatus }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.vertragsbeginn
            ? { vertragsbeginn: cloudFlags.vertragsbeginn }
            : {}),
        ...(cloudFlags.loaded && cloudFlags.mandantNr && cloudFlags.mandantNr !== 'WIEDERHERGESTELLT'
            ? { mandantNr: cloudFlags.mandantNr }
            : {}),
    };
    const tarifBadge = resolveTarifBadgeDisplay({
        mandantNr: tarifClientSource?.mandantNr || clientData?.mandantNr,
        client: tarifClientSource,
        settings,
        isLegacy: !!clientData?.isLegacy,
        isSolo:
            tarifClientSource.isSolo === true
                ? true
                : tarifClientSource.isSolo === false
                  ? false
                  : clientData?.isSolo === true
                    ? true
                    : clientData?.isSolo === false
                      ? false
                      : undefined,
        hasEmployeeEval:
            tarifClientSource.hasEmployeeEval === true
                ? true
                : tarifClientSource.hasEmployeeEval === false
                  ? false
                  : clientData?.hasEmployeeEval === true
                    ? true
                    : clientData?.hasEmployeeEval === false
                      ? false
                      : undefined,
        forceSoloTarif: !!clientData?.forceSoloTarif,
        nettoMonatsumsatz:
            clientData?.aggregatedNettoMonatsumsatz ?? clientData?.nettoMonatsumsatz ?? null,
    });
    const selectedPackage: ContractPackage = tarifBadge
        ? STATUS_TO_PACKAGE[tarifBadge.status]
        : resolveTarifTier({ settings, client: tarifClientSource });

    const mandantTypeLabel: Record<ContractPackage, string> = {
        solo: 'PEOne Solo',
        teamBase: 'PEOne TeamBase',
        teamPro: 'PEOne TeamPro',
        teamProPlus: 'PEOne TeamPro+',
    };
    const mandantType = mandantTypeLabel[selectedPackage] || 'Nicht zugewiesen';

    const isProBono = !!clientData?.proBonoActive || !!clientData?.proBonoChecked;
    const proBonoEnd =
        clientData?.proBonoUntil ||
        clientData?.proBonoEndDate ||
        null;

    const unlockedToolIds =
        (Array.isArray(unlockedPremiumTools) && unlockedPremiumTools.length > 0
            ? unlockedPremiumTools
            : clientData?.unlockedPremiumTools) || [];
    const premiumPlanMap =
        (clientData?.premiumToolPlanType && typeof clientData.premiumToolPlanType === 'object'
            ? (clientData.premiumToolPlanType as Record<string, string>)
            : null);
    const premiumActivatedMap =
        (clientData?.premiumToolActivatedAt && typeof clientData.premiumToolActivatedAt === 'object'
            ? (clientData.premiumToolActivatedAt as Record<string, string>)
            : null);
    const premiumContractLines = buildPremiumContractLines(
        unlockedToolIds,
        premiumPlanMap,
        premiumActivatedMap
    );
    const premiumSollMonthly = premiumContractLines.reduce(
        (sum, line) => sum + (Number(line.monthlyEquivalent) || 0),
        0
    );
    // Tools bis Stripe-Start immer wie Pro Bono ausweisen (Soll sichtbar, Abrechnung 0 €)
    const premiumToolsProBono = isProBono || !PREMIUM_CONTRACT_BILLING_ENABLED;

    let godModeId: string | null = null;
    try {
        godModeId = sessionStorage.getItem('GOD_MODE_ID');
    } catch {
        godModeId = null;
    }
    const crmMatch = godModeId
        ? (useAdminStore.getState().crmClients || []).find((c: any) => c.id === godModeId) ||
          (useAdminStore.getState().pendingApprovals || []).find((c: any) => c.id === godModeId)
        : null;

    // Cloud-Paar hat Vorrang; ohne Manual-Override entscheidet das Jahr aus der Nummer
    const forceNewCustomerPremium = cloudFlags.loaded
        ? cloudFlags.forceNewCustomerPremium
        : flagTrue(clientData?.forceNewCustomerPremium) ||
          flagTrue(crmMatch?.forceNewCustomerPremium) ||
          flagTrue(crmMatch?.clientData?.forceNewCustomerPremium);

    const disableNewCustomerPremium = cloudFlags.loaded
        ? cloudFlags.disableNewCustomerPremium
        : flagTrue(clientData?.disableNewCustomerPremium) ||
          flagTrue(crmMatch?.disableNewCustomerPremium) ||
          flagTrue(crmMatch?.clientData?.disableNewCustomerPremium);

    const neukundenManualOverride = cloudFlags.loaded
        ? cloudFlags.neukundenManualOverride
        : flagTrue(clientData?.neukundenManualOverride) ||
          flagTrue(crmMatch?.neukundenManualOverride) ||
          flagTrue(crmMatch?.clientData?.neukundenManualOverride);

    const billingStatus =
        (cloudFlags.loaded ? cloudFlags.billingStatus : null) ||
        clientData?.billingStatus ||
        crmMatch?.billingStatus ||
        crmMatch?.clientData?.billingStatus ||
        null;
    const customerNumberCloud =
        (cloudFlags.loaded ? cloudFlags.customerNumber : null) ||
        clientData?.customerNumber ||
        crmMatch?.customerNumber ||
        null;
    const mandantNrRaw = String(
        clientData?.mandantNr ||
            crmMatch?.mandantNr ||
            crmMatch?.clientData?.mandantNr ||
            ''
    ).trim();
    const isCorruptPlaceholderNr =
        !mandantNrRaw ||
        mandantNrRaw === 'WIEDERHERGESTELLT' ||
        mandantNrRaw === '---' ||
        /^warte/i.test(mandantNrRaw);

    // Kaputte Sync-Akte (WIEDERHERGESTELLT) = noch nicht freigegeben → 0 € anzeigen
    const isBillingPending =
        billingStatus === 'pending_approval' ||
        customerNumberCloud === 'PENDING' ||
        isCorruptPlaceholderNr ||
        (billingStatus !== 'active' &&
            !clientData?.isLegacy &&
            (cloudFlags.isExistingCustomer === false ||
                cloudFlags.isBestandskunde === false ||
                clientData?.isExistingCustomer === false ||
                clientData?.isBestandskunde === false));

    // Neukunde auch bei fehlenden Flags, wenn Akte noch PENDING/korrupt
    const isNewCustomerResolved = resolvePriceIsNewCustomer({
        client: {
            ...(clientData || {}),
            forceNewCustomerPremium,
            disableNewCustomerPremium,
            neukundenManualOverride,
            ...(cloudFlags.loaded && cloudFlags.isExistingCustomer !== null
                ? { isExistingCustomer: cloudFlags.isExistingCustomer }
                : {}),
            ...(cloudFlags.loaded && cloudFlags.isBestandskunde !== null
                ? { isBestandskunde: cloudFlags.isBestandskunde }
                : {}),
            ...(cloudFlags.loaded && cloudFlags.customerNumber
                ? { customerNumber: cloudFlags.customerNumber }
                : {}),
            ...(isCorruptPlaceholderNr || customerNumberCloud === 'PENDING'
                ? { customerNumber: 'PENDING', isExistingCustomer: false, isBestandskunde: false }
                : {}),
        },
        forceNewCustomerPremium,
        disableNewCustomerPremium,
        neukundenManualOverride,
        isNewCustomer:
            isCorruptPlaceholderNr || customerNumberCloud === 'PENDING'
                ? true
                : undefined,
        mandantNr: mandantNrRaw,
    });
    const isNewCustomer = isNewCustomerResolved;

    const neukundenAufschlagPct = readNeukundenPct(settings);

    const isLegacyClient = !!(clientData?.isLegacy);
    const storedFeeRaw =
        cloudFlags.monthlyFee ||
        clientData?.monthlyFee ||
        crmMatch?.monthlyFee ||
        crmMatch?.clientData?.monthlyFee;
    const storedFee =
        typeof storedFeeRaw === 'number'
            ? storedFeeRaw
            : parseFloat(String(storedFeeRaw ?? '').replace(',', '.')) || 0;

    // 1) Basispreis OHNE Aufschlag — Client-Flags hier bewusst neutralisiert,
    // sonst würde ein force-Flag im Store den Aufschlag doppelt anwenden.
    const bareFee = calculateClientMonthlyFee({
        settings,
        client: clientData
            ? {
                  ...clientData,
                  forceNewCustomerPremium: false,
                  isPremium2027: false,
                  disableNewCustomerPremium: true,
                  isBestandskunde: true,
              }
            : clientData,
        lizenzTyp: clientData?.lizenzDetails?.typ || (isBasic ? 'basic' : 'insider'),
        isProBono: false,
        tier: selectedPackage,
        isSolo: selectedPackage === 'solo',
        hasEmployeeEval: selectedPackage === 'teamPro' || selectedPackage === 'teamProPlus',
        wantsEvaluation: selectedPackage === 'teamPro' || selectedPackage === 'teamProPlus',
        nettoMonatsumsatz:
            clientData?.aggregatedNettoMonatsumsatz ?? clientData?.nettoMonatsumsatz ?? null,
        // Aufschlag hier bewusst AUS — wir rechnen ihn darunter explizit drauf
        disableNewCustomerPremium: true,
        forceNewCustomerPremium: false,
        isNewCustomer: false,
    });

    // Aufschlag folgt dem einen Häkchen (resolvePriceIsNewCustomer)
    const applyNeukunden = isNewCustomer;

    const liveFee =
        applyNeukunden && neukundenAufschlagPct > 0
            ? Math.round(bareFee * (1 + neukundenAufschlagPct / 100) * 100) / 100
            : bareFee;

    // Nicht-Legacy: Live-Berechnung gewinnt — gespeicherte (ggf. veraltete) monthlyFee
    // nur als Fallback, wenn live nichts berechenbar ist.
    const basePrice = isLegacyClient
        ? storedFee
        : liveFee > 0
          ? liveFee
          : storedFee;

    // Diagnose: flache Einzeiler-Ausgabe, loggt nur bei Wertänderung
    const debugLine =
        `[Vertragsübersicht] Preis-Debug | uid=${godModeId ? 'GOD_MODE' : 'auth'}` +
        ` | cloud: loaded=${cloudFlags.loaded} force=${cloudFlags.forceNewCustomerPremium} disable=${cloudFlags.disableNewCustomerPremium} premium2027=${cloudFlags.isPremium2027} fee=${cloudFlags.monthlyFee}` +
        ` | store: force=${String(clientData?.forceNewCustomerPremium)} premium2027=${String(clientData?.isPremium2027)} bestand=${String(clientData?.isBestandskunde)}` +
        ` | nr=${clientData?.mandantNr} paket=${selectedPackage}` +
        ` | isNewCustomer=${isNewCustomer} force=${forceNewCustomerPremium} disable=${disableNewCustomerPremium} pct=${neukundenAufschlagPct}` +
        ` | bare=${bareFee} live=${liveFee} stored=${storedFee} FINAL=${basePrice}`;
    useEffect(() => {
        console.info(debugLine);
    }, [debugLine]);
    
    const mwstSatz = settings?.country?.MwSt_Satz ?? 19;
    // PE-Lizenzgebühr / Stripe-Billing: immer EUR (nicht Salon-Währung CHF)
    const currency = 'EUR';

    // pending_approval: aktueller Beitrag 0; liveFee bleibt als Freigabe-Vorschau
    const netAmount = isBillingPending ? 0 : basePrice;
    const grossAmount = netAmount * (1 + mwstSatz / 100);
    const previewAfterApproval = basePrice;

    const mindestlaufzeit = (() => {
        const start =
            resolveVertragsbeginnDisplay({
                ...(clientData || {}),
                vertragsbeginn:
                    cloudFlags.vertragsbeginn ||
                    clientData?.vertragsbeginn ||
                    clientData?.clientData?.vertragsbeginn,
                mandantNr:
                    (cloudFlags.mandantNr && cloudFlags.mandantNr !== 'WIEDERHERGESTELLT'
                        ? cloudFlags.mandantNr
                        : null) ||
                    clientData?.mandantNr,
                isBestandskunde:
                    cloudFlags.isBestandskunde === true || clientData?.isBestandskunde === true,
            }) ||
            cloudFlags.vertragsbeginn ||
            null;

        if (!start || !/^\d{2}\/\d{4}$/.test(start)) {
            return (
                clientData?.mindestlaufzeit ||
                clientData?.lizenzDetails?.laeuftAb ||
                clientData?.vertragEndDatum ||
                clientData?.vertragsEnde ||
                'Unbekannt'
            );
        }
        // Mindestlaufzeit = 12 Monate ab Vertragsbeginn
        const [mmStr, yyyyStr] = start.split('/');
        let endMonth = parseInt(mmStr, 10);
        let endYear = parseInt(yyyyStr, 10) + 1;
        if (!isNaN(endMonth) && !isNaN(endYear) && endMonth >= 1 && endMonth <= 12) {
            return `${String(endMonth).padStart(2, '0')}/${endYear}`;
        }
        return start;
    })();

    const coaches = (team || []).filter((member: any) => member.category === 'coach' && member.name);
    
    const provider = { firma: 'PreisExpert', name: 'Christian Funk', strasse: 'Rotehahnstraße 2', plzOrt: '21335 Lüneburg' };
    const client = clientData;

    const status = { confirmed: clientData?.contractConfirmed || false };

    return (
        <Page>
            <CombinedStyles />
            <main className="contract-carbon-shell font-sans text-pe-text dark:text-slate-200 selection:bg-[#E6C299] selection:text-black p-4 sm:p-8">
                
                <div className="max-w-4xl mx-auto mb-16">
                    <h1 className="text-3xl font-black tracking-tight mb-8 uppercase text-pe-text dark:text-white font-sans carbon-weave-bg rounded-xl border border-pe-border dark:border-[#2a2a2a] px-6 py-4">Vertragsübersicht</h1>

                    <div className={`rounded-xl p-6 mb-6 flex flex-col justify-center items-start border carbon-panel bg-pe-card dark:bg-[#030303] ${isProBono ? 'border-pink-500/40 shadow-[0_0_25px_rgba(244,114,182,0.15)]' : 'border-pe-border dark:border-[#20232b]'}`}>
                        <div className="flex justify-between w-full items-start mb-4 carbon-content gap-3 flex-wrap">
                            <h2 className="text-xs font-bold text-pe-muted dark:text-slate-400 uppercase tracking-wider font-sans px-3 py-2 carbon-weave-bg rounded-lg">Aktueller Monatsbeitrag</h2>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                {isBillingPending && (
                                    <span className="px-3 py-1 bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs rounded-full border border-amber-500/40 font-sans font-semibold">
                                        Abrechnung pausiert (Akte in Prüfung)
                                    </span>
                                )}
                                <span className="px-3 py-1 bg-stone-200/60 dark:bg-black/60 text-pe-muted dark:text-slate-300 text-xs rounded-full border border-pe-border dark:border-[#22252e] font-sans font-semibold">Mandant: {mandantType}</span>
                            </div>
                        </div>

                        <div className="w-full carbon-content">
                            {isProBono ? (
                                <div className="flex flex-col gap-2 w-full">
                                    <div className="flex items-baseline gap-2 opacity-40 line-through text-pe-muted dark:text-slate-400">
                                        <span className="text-3xl font-bold font-mono-numbers tracking-tight">{(isBillingPending ? previewAfterApproval : netAmount).toFixed(2)}</span>
                                        <span className="text-lg font-mono-numbers">{currency}</span>
                                    </div>
                                    <div className="text-pink-900 dark:text-pink-300 font-medium bg-pink-100/70 dark:bg-pink-950/20 p-4 rounded-xl border border-pink-300 dark:border-pink-900/40 mt-2 font-sans text-sm leading-relaxed shadow-inner">
                                        Dein regulärer Preis wäre aktuell <span className="font-bold font-mono-numbers">{(isBillingPending ? previewAfterApproval : netAmount).toFixed(2)} {currency}</span> (Netto)
                                        {applyNeukunden && neukundenAufschlagPct > 0 && (
                                            <> — inkl. <span className="font-mono-numbers font-bold">{neukundenAufschlagPct}%</span> Neukunden-Aufschlag</>
                                        )}
                                        . <br/>
                                        Bis zum {proBonoEnd ? <span className="font-bold text-pe-text dark:text-white bg-pink-100 dark:bg-pink-900/40 px-1.5 py-0.5 rounded border border-pink-300 dark:border-pink-500/30">{proBonoEnd}</span> : 'auf Weiteres'} betreuen wir Dich kostenfrei, um Dich bei Deinen Herausforderungen zu unterstützen!
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-baseline gap-2 text-pe-accent-text dark:text-[#E6C299] led-gold">
                                        <span className="text-5xl font-black font-mono-numbers tracking-tight">{netAmount.toFixed(2)}</span>
                                        <span className="text-2xl font-bold font-mono-numbers">{currency}</span>
                                        <span className="text-xs ml-2 opacity-60 font-sans font-bold uppercase tracking-wider">/ (Netto)</span>
                                    </div>
                                    {!isBillingPending && applyNeukunden && neukundenAufschlagPct > 0 && (
                                        <div className="mt-2 text-[#8D6435]/80 dark:text-[#E6C299]/80 font-sans text-xs font-semibold">
                                            Inkl. {neukundenAufschlagPct}% Neukunden-Aufschlag
                                        </div>
                                    )}
                                    <div className="mt-2 text-pe-muted dark:text-slate-400 font-mono-numbers text-xs font-semibold">
                                        inkl. {mwstSatz}% MwSt: <span className="text-pe-text dark:text-white font-bold">{grossAmount.toFixed(2)} {currency}</span> (Brutto)
                                    </div>
                                    {isBillingPending && (
                                        <div className="mt-3 text-amber-800 dark:text-amber-200/90 font-sans text-xs font-semibold leading-relaxed">
                                            Voraussichtlicher Beitrag nach Coach-Freigabe:{' '}
                                            <span className="font-mono-numbers font-bold">
                                                {previewAfterApproval.toFixed(2)} {currency}
                                            </span>
                                            {applyNeukunden && neukundenAufschlagPct > 0 && (
                                                <> (inkl. {neukundenAufschlagPct}% Neukunden-Aufschlag)</>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {premiumContractLines.length > 0 && (
                        <div
                            className={`rounded-xl p-6 mb-6 flex flex-col justify-center items-start border carbon-panel bg-pe-card dark:bg-[#030303] ${
                                premiumToolsProBono
                                    ? 'border-pink-500/40 shadow-[0_0_25px_rgba(244,114,182,0.12)]'
                                    : 'border-pe-border dark:border-[#20232b]'
                            }`}
                        >
                            <div className="flex justify-between w-full items-start mb-4 carbon-content gap-3 flex-wrap">
                                <h2 className="text-xs font-bold text-pe-muted dark:text-slate-400 uppercase tracking-wider font-sans px-3 py-2 carbon-weave-bg rounded-lg">
                                    Premium-Tools · Abrechnung
                                </h2>
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                    {premiumToolsProBono && (
                                        <span className="px-3 py-1 bg-pink-500/15 text-pink-800 dark:text-pink-300 text-xs rounded-full border border-pink-500/40 font-sans font-semibold">
                                            Pro Bono
                                        </span>
                                    )}
                                    <span className="px-3 py-1 bg-stone-200/60 dark:bg-black/60 text-pe-muted dark:text-slate-300 text-xs rounded-full border border-pe-border dark:border-[#22252e] font-sans font-semibold">
                                        {premiumContractLines.length} Tool
                                        {premiumContractLines.length === 1 ? '' : 's'} aktiv
                                    </span>
                                </div>
                            </div>

                            <div className="w-full carbon-content space-y-3">
                                {premiumContractLines.map((line) => (
                                    <div
                                        key={line.toolId}
                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-pe-border dark:border-[#22252e] bg-pe-sunken/40 dark:bg-black/30 px-4 py-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-sans font-bold text-pe-text dark:text-white tracking-wide">
                                                {line.toolName}
                                            </div>
                                            <div className="text-[11px] text-pe-muted dark:text-slate-500 font-sans mt-0.5">
                                                {line.planLabel}
                                                {line.activatedAt
                                                    ? ` · aktiv seit ${formatPremiumActivatedDate(line.activatedAt)}`
                                                    : ''}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            {premiumToolsProBono ? (
                                                <>
                                                    <div className="opacity-40 line-through text-pe-muted dark:text-slate-400 font-mono-numbers text-sm">
                                                        {line.listPrice.toFixed(2)} {currency}
                                                        <span className="text-[10px] ml-1 uppercase tracking-wider">
                                                            {line.planType === 'yearly' ? '/ Jahr' : '/ Monat'}
                                                        </span>
                                                    </div>
                                                    <div className="text-pink-800 dark:text-pink-300 font-mono-numbers font-bold text-sm">
                                                        0,00 {currency}
                                                        <span className="text-[10px] ml-1 font-sans font-semibold uppercase tracking-wider opacity-80">
                                                            Pro Bono
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-pe-accent-text dark:text-[#E6C299] font-mono-numbers font-bold text-lg">
                                                    {line.listPrice.toFixed(2)} {currency}
                                                    <span className="text-[10px] ml-1 opacity-70 uppercase tracking-wider">
                                                        {line.planType === 'yearly' ? '/ Jahr' : '/ Monat'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-pe-border dark:border-[#22252e]">
                                    <span className="text-xs font-sans font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                                        Summe Soll (Monatsäquivalent)
                                    </span>
                                    {premiumToolsProBono ? (
                                        <div className="text-right">
                                            <span className="opacity-40 line-through text-pe-muted dark:text-slate-400 font-mono-numbers text-sm mr-2">
                                                {premiumSollMonthly.toFixed(2)} {currency}
                                            </span>
                                            <span className="text-pink-800 dark:text-pink-300 font-mono-numbers font-black">
                                                0,00 {currency}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-pe-accent-text dark:text-[#E6C299] font-mono-numbers font-black">
                                            {premiumSollMonthly.toFixed(2)} {currency}
                                        </span>
                                    )}
                                </div>

                                {premiumToolsProBono && (
                                    <div className="text-pink-900 dark:text-pink-300 font-medium bg-pink-100/70 dark:bg-pink-950/20 p-4 rounded-xl border border-pink-300 dark:border-pink-900/40 font-sans text-sm leading-relaxed shadow-inner">
                                        Die freigeschalteten Premium-Tools sind bereits in der Abrechnung erfasst
                                        (Sollpreis oben). Aktuell rechnen wir sie{' '}
                                        <span className="font-bold">Pro Bono mit 0,00 {currency}</span> ab
                                        {proBonoEnd ? (
                                            <>
                                                {' '}
                                                — analog zum Vertrag bis{' '}
                                                <span className="font-bold text-pe-text dark:text-white bg-pink-100 dark:bg-pink-900/40 px-1.5 py-0.5 rounded border border-pink-300 dark:border-pink-500/30">
                                                    {proBonoEnd}
                                                </span>
                                            </>
                                        ) : (
                                            <> — bis die kostenpflichtige Tool-Abrechnung freigeschaltet wird</>
                                        )}
                                        .
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="border border-pe-border dark:border-[#20232b] mb-6 rounded-xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4 carbon-panel bg-pe-card dark:bg-[#030303]">
                        <div className="flex-1 carbon-content">
                            <h3 className="font-black text-lg text-pe-text dark:text-white mb-1 font-sans uppercase tracking-wide px-3 py-2 carbon-weave-bg rounded-lg inline-block">Zahlungsverwaltung</h3>
                            <p className="text-sm text-pe-muted dark:text-slate-400 font-sans leading-relaxed">Verwalte deine Rechnungen und Zahlungsmethoden sicher über unser Zahlungsportal.</p>
                        </div>
                        <button
                            className={`whitespace-nowrap px-6 py-3 border rounded-md transition-all font-bold text-xs uppercase tracking-wider shadow-md h-fit relative z-10 ${
                                isBillingPending
                                    ? 'bg-stone-100 dark:bg-black/40 border-pe-border dark:border-[#22252e] text-pe-muted dark:text-slate-500 cursor-not-allowed opacity-60'
                                    : 'bg-stone-200 dark:bg-slate-800 border-pe-border-strong dark:border-slate-700 hover:bg-stone-300 dark:hover:bg-slate-700 text-pe-text dark:text-slate-100'
                            }`}
                            type="button"
                            disabled={isBillingPending}
                            title={
                                isBillingPending
                                    ? 'Abrechnung pausiert — Stripe-Portal erst nach Coach-Freigabe verfügbar'
                                    : undefined
                            }
                        >
                            Zu meinen Rechnungen / Stripe Portal
                        </button>
                    </div>

                    <div className="border border-pe-border dark:border-[#20232b] rounded-xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4 carbon-panel bg-pe-card dark:bg-[#030303]">
                        <div className="flex-1 carbon-content">
                            <h3 className="font-black text-lg text-pe-muted dark:text-slate-300 mb-1 font-sans uppercase tracking-wide px-3 py-2 carbon-weave-bg rounded-lg inline-block">Vertragsverwaltung</h3>
                            <p className="text-sm text-stone-500 dark:text-slate-500 font-sans leading-relaxed">Hier kannst du alle Optionen zur Beendigung deines aktuellen Mandats einsehen.</p>
                            
                            <div className="mt-4 inline-flex items-center gap-2 bg-pe-sunken dark:bg-[#050505] border border-pe-border dark:border-[#20232b] px-3 py-1.5 rounded-lg shadow-inner">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="text-stone-500 dark:text-slate-500" viewBox="0 0 256 256">
                                    <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm64-88a8,8,0,0,1-8,8H128a8,8,0,0,1-8-8V72a8,8,0,0,1,16,0v48h48A8,8,0,0,1,192,128Z"></path>
                                </svg>
                                <span className="text-[10px] font-bold text-pe-muted dark:text-slate-400 uppercase tracking-widest">Mindestlaufzeit bis:</span>
                                <span className="text-xs font-black text-pe-accent-text dark:text-[#E6C299]">{mindestlaufzeit}</span>
                            </div>
                        </div>
                        <button className="whitespace-nowrap px-6 py-3 bg-stone-100/60 dark:bg-black/40 border border-pe-border dark:border-[#22252e] hover:bg-stone-200 dark:hover:bg-slate-900/40 text-red-600 dark:text-red-400 font-bold text-xs uppercase tracking-wider rounded-md transition-all shadow-md h-fit relative z-10" type="button">
                            Kündigung einleiten
                        </button>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto carbon-panel p-6 lg:p-10 bg-pe-card dark:bg-[#030303]">
                    <div className="carbon-content">
                        <Section>
                            <h2 className="text-xl font-bold text-center mb-6 text-pe-accent-text font-sans tracking-wide uppercase text-sm px-4 py-3 carbon-weave-bg rounded-lg border border-pe-border dark:border-[#2a2a2a]">Nutzungs- & Beratungsvertrag</h2>
                            <div className="space-y-4">
                                <p className="text-center text-lg mb-4 text-pe-muted dark:text-slate-300">Zwischen</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base border-b-2 border-pe-border-strong dark:border-[#333] pb-6">
                                    <div className="metal-badge bg-stone-100/70 dark:bg-black/70 p-5 rounded-xl">
                                        <h3 className="font-black text-pe-text dark:text-white mb-2 uppercase tracking-wider">Auftraggeber/in:</h3>
                                        <p className="text-pe-muted dark:text-slate-300">{client?.unternehmername || 'N/A'}</p> 
                                        <p className="text-pe-muted dark:text-slate-300">{client?.unternehmen || client?.salonname || 'N/A'}</p> 
                                        <p className="text-pe-muted dark:text-slate-300">{client?.strasse || 'N/A'}, {client?.plz || 'N/A'} {client?.ort || 'N/A'}</p> 
                                        <p className="mt-2 text-pe-muted dark:text-slate-400">
                                            <span className="font-semibold text-pe-accent-text dark:text-[#E6C299]">Mandatsreferenz:</span>{' '}
                                            <span className="font-mono">{client?.mandantNr || 'N/A'}</span>
                                        </p> 
                                    </div>
                                    <div className="metal-badge bg-stone-100/70 dark:bg-black/70 p-5 rounded-xl text-left md:text-right">
                                        <h3 className="font-black text-pe-text dark:text-white mb-2 uppercase tracking-wider">Auftragnehmer:</h3>
                                        <p className="text-pe-muted dark:text-slate-300">{provider.firma}</p> 
                                        <p className="text-pe-muted dark:text-slate-300">{provider.name}</p> 
                                        <p className="text-pe-muted dark:text-slate-300">{provider.strasse}, {provider.plzOrt}</p> 
                                    </div>
                                </div>
                                <p className="text-center pt-4 text-pe-muted dark:text-slate-300">wird der folgende Vertrag abgeschlossen:</p> 
                            </div>
                        </Section>

                        <Section>
                            <h2 className="text-xl font-bold text-center mb-6 text-pe-accent-text font-sans tracking-wide uppercase text-sm px-4 py-3 carbon-weave-bg rounded-lg border border-pe-border dark:border-[#2a2a2a]">Vertragsinhalt</h2>
                            <ContractBody
                                settings={settings}
                                coaches={coaches}
                                selectedPackage={selectedPackage}
                                variant="dashboard"
                                isNewCustomer={applyNeukunden}
                                isProBono={isProBono}
                                proBonoUntil={proBonoEnd}
                                effectiveMonthlyFee={basePrice}
                            />
                        </Section>

                        <Section title="Vertragsstatus">
                            <div className="metal-badge bg-stone-100/70 dark:bg-black/70 border border-pe-border-strong dark:border-[#333] rounded-xl p-5 flex items-center justify-center text-lg">
                                {status.confirmed ? (
                                    <p>Status: <span className="font-black text-emerald-400 uppercase tracking-wider">Vom Kunden bestätigt</span></p> 
                                ) : (
                                    <p>Status: <span className="font-black text-yellow-400 uppercase tracking-wider">Bestätigung ausstehend</span></p> 
                                )}
                            </div>
                        </Section>
                    </div>
                </div>
            </main>
        </Page>
    );
}
